import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDatepicker, START_DATE, OnDatesChangeProps, FocusedInput } from '@datepicker-react/hooks';
import { startOfDay, isSameMonth } from 'date-fns';
import { DatePickerContext } from './contexts/DatePickerContext';
import { GridContainer } from './components/GridContainer';
import { CalendarContainer } from './components/CalendarContainer';
import { CalendarControlContainer } from './components/CalendarControlContainer';
import Month from './components/Month';

interface IRangeCalendarProps {
  startDate: Date | null;
  endDate: Date | null;
  currentDay: Date | null;
  minBookingDays: number;
  onDateChange: (startDate: Date | null, endDate: Date | null, focusedInput?: FocusedInput | undefined) => void;
  className?: string;
  exactMinBookingDays: boolean;
  maxBookingDate?: Date;
  focusedInput?: FocusedInput;
  numberOfMonths?: number;
}

const RangeCalendar = ({
  startDate,
  endDate,
  currentDay,
  minBookingDays,
  onDateChange,
  exactMinBookingDays,
  maxBookingDate,
  focusedInput,
  numberOfMonths,
}: IRangeCalendarProps): JSX.Element => {
  const onDatesChange = (data: OnDatesChangeProps): void => {
    const { startDate, endDate, focusedInput } = data;

    onDateChange(startDate, endDate, focusedInput);
  };

  const {
    firstDayOfWeek,
    activeMonths,
    isDateSelected,
    isDateHovered,
    isFirstOrLastSelectedDate,
    isStartDate,
    isEndDate,
    isDateBlocked,
    isDateFocused,
    focusedDate,
    onDateHover,
    onDateSelect,
    onDateFocus,
    goToDate,
    goToPreviousMonths,
    goToNextMonths,
  } = useDatepicker({
    startDate,
    endDate,
    focusedInput: focusedInput || START_DATE,
    onDatesChange,
    numberOfMonths: numberOfMonths || 1,
    minBookingDays,
    exactMinBookingDays,
    maxBookingDate,
  });

  const goToMonth = useCallback(() => {
    if (startDate && !isSameMonth(startDate, activeMonths[0].date)) {
      goToDate(startDate || startOfDay(Date.now()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  useEffect(goToMonth, [startDate, goToMonth]);

  // TODO: This should be converted to use kibble
  return (
    <DatePickerContext.Provider
      value={{
        focusedDate,
        isDateFocused,
        isDateSelected,
        isDateHovered,
        isDateBlocked,
        isFirstOrLastSelectedDate,
        isStartDate,
        isEndDate,
        onDateSelect,
        onDateFocus,
        onDateHover,
      }}
    >
      <CalendarControlContainer>
        <CalendarContainer>
          <GridContainer>
            {activeMonths.map((month) => {
              return (
                <Month
                  key={`${month.year}-${month.month}`}
                  year={month.year}
                  month={month.month}
                  firstDayOfWeek={firstDayOfWeek}
                  currentDay={currentDay}
                  goToPreviousMonths={goToPreviousMonths}
                  goToNextMonths={goToNextMonths}
                />
              );
            })}
          </GridContainer>
        </CalendarContainer>
      </CalendarControlContainer>
    </DatePickerContext.Provider>
  );
};

RangeCalendar.propTypes = {
  date: PropTypes.instanceOf(Date),
  onDateChange: PropTypes.func.isRequired,
};

RangeCalendar.defaultProps = {
  date: null,
  displayFormat: 'MM/dd/yyyy',
};

export default RangeCalendar;
